import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  IconButton,
  TextField,
  useTheme,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

const AcademicPanel = ({ athlete, onUpdateAthlete }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedAthlete, setEditedAthlete] = useState(athlete);

  // Update editedAthlete when athlete prop changes
  useEffect(() => {
    setEditedAthlete(athlete);
  }, [athlete]);

  // Safety check - ensure athlete data is loaded
  if (!athlete || !athlete.academics) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Academic Profile
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Loading academic information...
        </Typography>
      </Paper>
    );
  }

  // Calculate GPA color
  const getGpaColor = (gpa) => {
    // Ensure gpa is a number
    const numGpa = typeof gpa === "number" ? gpa : parseFloat(gpa) || 0;
    if (numGpa >= 3.7) return theme.palette.success.main;
    if (numGpa >= 3.0) return theme.palette.success.light;
    if (numGpa >= 2.5) return theme.palette.warning.main;
    return theme.palette.error.light;
  };

  // Helper function to safely format GPA
  const formatGpa = (gpa) => {
    if (gpa === null || gpa === undefined || gpa === "" || gpa === "N/A")
      return "Not set";
    const numGpa = typeof gpa === "number" ? gpa : parseFloat(gpa);
    return isNaN(numGpa) ? "Not set" : numGpa.toFixed(1);
  };

  // Helper function to safely get numeric GPA
  const getNumericGpa = (gpa) => {
    if (gpa === null || gpa === undefined || gpa === "" || gpa === "N/A")
      return 0;
    const numGpa = typeof gpa === "number" ? gpa : parseFloat(gpa);
    return isNaN(numGpa) ? 0 : numGpa;
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original values
      setEditedAthlete(athlete);
    }
    setIsEditing(!isEditing);
  };

  // Handle save changes
  const handleSave = () => {
    if (onUpdateAthlete) {
      // Clean the data before sending to backend
      const cleanedAthlete = { ...editedAthlete };

      // Clean academics data - remove "N/A" and convert empty strings to null
      if (cleanedAthlete.academics) {
        // Handle GPA field
        if (
          cleanedAthlete.academics.gpa === "N/A" ||
          cleanedAthlete.academics.gpa === ""
        ) {
          cleanedAthlete.academics.gpa = null;
        } else if (
          cleanedAthlete.academics.gpa &&
          !isNaN(parseFloat(cleanedAthlete.academics.gpa))
        ) {
          cleanedAthlete.academics.gpa = parseFloat(
            cleanedAthlete.academics.gpa
          );
        } else {
          // If GPA is not a valid number, set to null
          cleanedAthlete.academics.gpa = null;
        }

        // Handle coreGPA field
        if (
          cleanedAthlete.academics.coreGpa === "N/A" ||
          cleanedAthlete.academics.coreGpa === ""
        ) {
          cleanedAthlete.academics.coreGpa = null;
        } else if (
          cleanedAthlete.academics.coreGpa &&
          !isNaN(parseFloat(cleanedAthlete.academics.coreGpa))
        ) {
          cleanedAthlete.academics.coreGpa = parseFloat(
            cleanedAthlete.academics.coreGpa
          );
        } else {
          // If coreGPA is not a valid number, set to null
          cleanedAthlete.academics.coreGpa = null;
        }
      }

      onUpdateAthlete(cleanedAthlete);
    }
    setIsEditing(false);
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setEditedAthlete((prev) => ({
      ...prev,
      academics: {
        ...prev.academics,
        [field]: value,
      },
    }));
  };

  // Render GPA progress bar
  const renderGpaProgress = (gpa, maxGpa = 4.0) => {
    const numGpa = getNumericGpa(gpa);
    const percentage = (numGpa / maxGpa) * 100;
    return (
      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <Box
            sx={{
              height: 8,
              borderRadius: 5,
              backgroundColor: theme.palette.grey[200],
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${percentage}%`,
                backgroundColor: getGpaColor(numGpa),
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">
            {formatGpa(numGpa)}/{maxGpa}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
        >
          <SchoolIcon sx={{ mr: 1 }} color="primary" />
          Academic Profile
        </Typography>
        <Box>
          {isEditing ? (
            <>
              <IconButton
                size="small"
                onClick={handleSave}
                color="primary"
                sx={{ mr: 1 }}
                title="Save changes"
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleEditToggle}
                color="secondary"
                title="Cancel editing"
              >
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton
              size="small"
              onClick={handleEditToggle}
              color="primary"
              title="Edit academic profile"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {/* Academic Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              bgcolor: "background.default",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cumulative GPA
            </Typography>
            {isEditing ? (
              <TextField
                value={editedAthlete.academics?.gpa || ""}
                onChange={(e) => handleInputChange("gpa", e.target.value)}
                size="small"
                variant="outlined"
                placeholder="Enter GPA"
                sx={{ mb: 1 }}
                inputProps={{
                  step: 0.01,
                  min: 0,
                  max: 4.0,
                }}
              />
            ) : (
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: getGpaColor(athlete.academics?.gpa),
                }}
              >
                {formatGpa(athlete.academics?.gpa)}
              </Typography>
            )}
            {athlete.academics?.gpa &&
              athlete.academics.gpa !== "N/A" &&
              renderGpaProgress(athlete.academics?.gpa)}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              bgcolor: "background.default",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Core GPA (NCAA)
            </Typography>
            {isEditing ? (
              <TextField
                value={editedAthlete.academics?.coreGpa || ""}
                onChange={(e) => handleInputChange("coreGpa", e.target.value)}
                size="small"
                variant="outlined"
                placeholder="Enter Core GPA"
                sx={{ mb: 1 }}
                inputProps={{
                  step: 0.01,
                  min: 0,
                  max: 4.0,
                }}
              />
            ) : (
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: getGpaColor(athlete.academics?.coreGpa),
                }}
              >
                {formatGpa(athlete.academics?.coreGpa)}
              </Typography>
            )}
            {athlete.academics?.coreGpa &&
              athlete.academics.coreGpa !== "N/A" &&
              renderGpaProgress(athlete.academics?.coreGpa)}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AcademicPanel;
